import { useState } from "react";
import { PLATFORMS, orderInputSchema, type OrderInput } from "../../worker/schemas/order";
import { ApiError, submitCampaignOrder, type AppConfig } from "../lib/api";
import { TurnstileWidget } from "../components/TurnstileWidget";

type FormState = {
  artistName: string;
  songTitle: string;
  primaryGenre: string;
  description: string;
  email: string;
  lyrics: string;
  releaseDate: string;
  secondaryGenre: string;
  mood: string;
  targetAudience: string;
  influences: string;
  platforms: string[];
  importantLines: string;
  notes: string;
};

const EMPTY_FORM: FormState = {
  artistName: "",
  songTitle: "",
  primaryGenre: "",
  description: "",
  email: "",
  lyrics: "",
  releaseDate: "",
  secondaryGenre: "",
  mood: "",
  targetAudience: "",
  influences: "",
  platforms: [],
  importantLines: "",
  notes: "",
};

const LIMITS: Partial<Record<keyof FormState, number>> = {
  artistName: 120,
  songTitle: 120,
  primaryGenre: 60,
  description: 2000,
  lyrics: 12000,
  secondaryGenre: 60,
  mood: 200,
  targetAudience: 300,
  influences: 500,
  importantLines: 1000,
  notes: 2000,
};

function toOrderInput(form: FormState, turnstileToken: string | null): unknown {
  return {
    artistName: form.artistName,
    songTitle: form.songTitle,
    primaryGenre: form.primaryGenre,
    description: form.description,
    email: form.email,
    lyrics: form.lyrics || undefined,
    releaseDate: form.releaseDate || undefined,
    secondaryGenre: form.secondaryGenre || undefined,
    mood: form.mood || undefined,
    targetAudience: form.targetAudience || undefined,
    influences: form.influences || undefined,
    platforms: form.platforms,
    importantLines: form.importantLines || undefined,
    notes: form.notes || undefined,
    turnstileToken: turnstileToken ?? undefined,
  };
}

function TextField({
  name,
  label,
  value,
  onChange,
  error,
  required,
  hint,
  type = "text",
}: {
  name: keyof FormState;
  label: string;
  value: string;
  onChange: (v: string) => void;
  error?: string;
  required?: boolean;
  hint?: string;
  type?: "text" | "email" | "date";
}) {
  return (
    <div className="field">
      <label htmlFor={name}>
        {label} {required ? <span aria-hidden="true">*</span> : null}
      </label>
      <input
        id={name}
        name={name}
        type={type}
        value={value}
        required={required}
        maxLength={LIMITS[name]}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `${name}-error` : undefined}
      />
      {hint && !error ? <span className="hint">{hint}</span> : null}
      {error ? (
        <span className="error" id={`${name}-error`}>
          {error}
        </span>
      ) : null}
    </div>
  );
}

function TextAreaField({
  name,
  label,
  value,
  onChange,
  error,
  required,
  hint,
}: {
  name: keyof FormState;
  label: string;
  value: string;
  onChange: (v: string) => void;
  error?: string;
  required?: boolean;
  hint?: string;
}) {
  const limit = LIMITS[name];
  return (
    <div className="field">
      <label htmlFor={name}>
        {label} {required ? <span aria-hidden="true">*</span> : null}
      </label>
      <textarea
        id={name}
        name={name}
        value={value}
        required={required}
        maxLength={limit}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `${name}-error` : undefined}
      />
      {limit ? (
        <span className="char-count">
          {value.length} / {limit}
        </span>
      ) : null}
      {hint && !error ? <span className="hint">{hint}</span> : null}
      {error ? (
        <span className="error" id={`${name}-error`}>
          {error}
        </span>
      ) : null}
    </div>
  );
}

export function BuildCampaign({ config }: { config: AppConfig | null }) {
  const [step, setStep] = useState<"form" | "review">("form");
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function togglePlatform(platform: string) {
    setForm((prev) => ({
      ...prev,
      platforms: prev.platforms.includes(platform)
        ? prev.platforms.filter((p) => p !== platform)
        : [...prev.platforms, platform],
    }));
  }

  function goToReview() {
    const result = orderInputSchema.safeParse(toOrderInput(form, turnstileToken));
    if (!result.success) {
      const fieldErrors: Partial<Record<keyof FormState, string>> = {};
      for (const issue of result.error.issues) {
        const key = issue.path[0] as keyof FormState | undefined;
        if (key && !fieldErrors[key]) fieldErrors[key] = issue.message;
      }
      setErrors(fieldErrors);
      const firstKey = result.error.issues[0]?.path[0];
      if (typeof firstKey === "string") {
        document.getElementById(firstKey)?.scrollIntoView({ block: "center" });
      }
      return;
    }
    setErrors({});
    setStep("review");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function handleGenerate() {
    setSubmitError(null);
    const result = orderInputSchema.safeParse(toOrderInput(form, turnstileToken));
    if (!result.success) {
      setStep("form");
      return;
    }
    setSubmitting(true);
    try {
      const res = await submitCampaignOrder(result.data as OrderInput);
      window.location.href = res.checkoutUrl;
    } catch (err) {
      setSubmitting(false);
      if (err instanceof ApiError) {
        setSubmitError(err.message);
      } else {
        setSubmitError("Something went wrong. Please try again.");
      }
    }
  }

  const priceDisplay = ((config?.displayPriceCents ?? 999) / 100).toFixed(2);

  return (
    <section className="section-tight">
      <div className="container" style={{ maxWidth: 720 }}>
        <div
          className="progress-track"
          role="progressbar"
          aria-valuenow={step === "form" ? 1 : 2}
          aria-valuemin={1}
          aria-valuemax={2}
        >
          <div className={`progress-step ${step === "form" ? "is-active" : "is-done"}`} />
          <div className={`progress-step ${step === "review" ? "is-active" : ""}`} />
        </div>

        {step === "form" ? (
          <>
            <div
              className="section-head"
              style={{ textAlign: "left", margin: "0 0 32px" }}
            >
              <span className="eyebrow">Step 1 of 2</span>
              <h2>Tell us about your song</h2>
              <p>The more detail you give, the more specific your campaign will be.</p>
            </div>

            <form
              className="form-grid"
              onSubmit={(e) => {
                e.preventDefault();
                goToReview();
              }}
            >
              <div className="grid grid-2">
                <TextField
                  name="artistName"
                  label="Artist / Band Name"
                  required
                  value={form.artistName}
                  onChange={(v) => set("artistName", v)}
                  error={errors.artistName}
                />
                <TextField
                  name="songTitle"
                  label="Song Title"
                  required
                  value={form.songTitle}
                  onChange={(v) => set("songTitle", v)}
                  error={errors.songTitle}
                />
              </div>
              <div className="grid grid-2">
                <TextField
                  name="primaryGenre"
                  label="Primary Genre"
                  required
                  value={form.primaryGenre}
                  onChange={(v) => set("primaryGenre", v)}
                  error={errors.primaryGenre}
                />
                <TextField
                  name="secondaryGenre"
                  label="Secondary Genre"
                  value={form.secondaryGenre}
                  onChange={(v) => set("secondaryGenre", v)}
                  error={errors.secondaryGenre}
                />
              </div>

              <TextAreaField
                name="description"
                label="Song Description / Meaning"
                required
                value={form.description}
                onChange={(v) => set("description", v)}
                error={errors.description}
                hint="What is the song about? What should the campaign emphasize?"
              />

              <TextField
                name="email"
                label="Your Email"
                type="email"
                required
                value={form.email}
                onChange={(v) => set("email", v)}
                error={errors.email}
                hint="Your campaign will be delivered here."
              />

              <TextAreaField
                name="lyrics"
                label="Lyrics (optional)"
                value={form.lyrics}
                onChange={(v) => set("lyrics", v)}
                error={errors.lyrics}
              />
              <TextAreaField
                name="importantLines"
                label="Important Lyric Lines (optional)"
                value={form.importantLines}
                onChange={(v) => set("importantLines", v)}
                error={errors.importantLines}
              />

              <div className="grid grid-2">
                <TextField
                  name="releaseDate"
                  label="Release Date (optional)"
                  type="date"
                  value={form.releaseDate}
                  onChange={(v) => set("releaseDate", v)}
                  error={errors.releaseDate}
                />
                <TextField
                  name="mood"
                  label="Mood (optional)"
                  value={form.mood}
                  onChange={(v) => set("mood", v)}
                  error={errors.mood}
                />
              </div>
              <div className="grid grid-2">
                <TextField
                  name="targetAudience"
                  label="Target Audience (optional)"
                  value={form.targetAudience}
                  onChange={(v) => set("targetAudience", v)}
                  error={errors.targetAudience}
                />
                <TextField
                  name="influences"
                  label="Similar Artists / Influences (optional)"
                  value={form.influences}
                  onChange={(v) => set("influences", v)}
                  error={errors.influences}
                />
              </div>

              <div className="field">
                <label>Preferred Platforms (optional)</label>
                <div className="checkbox-grid">
                  {PLATFORMS.map((platform) => (
                    <label className="checkbox-pill" key={platform}>
                      <input
                        type="checkbox"
                        checked={form.platforms.includes(platform)}
                        onChange={() => togglePlatform(platform)}
                      />
                      {platform}
                    </label>
                  ))}
                </div>
              </div>

              <TextAreaField
                name="notes"
                label="Additional Notes (optional)"
                value={form.notes}
                onChange={(v) => set("notes", v)}
                error={errors.notes}
              />

              <button type="submit" className="btn btn-primary btn-block">
                Review My Order
              </button>
            </form>
          </>
        ) : (
          <>
            <div
              className="section-head"
              style={{ textAlign: "left", margin: "0 0 32px" }}
            >
              <span className="eyebrow">Step 2 of 2</span>
              <h2>Review your order</h2>
              <p>Double-check your details, then continue to secure checkout.</p>
            </div>

            <dl className="review-list card">
              <div className="review-row">
                <dt>Artist</dt>
                <dd>{form.artistName}</dd>
              </div>
              <div className="review-row">
                <dt>Song Title</dt>
                <dd>{form.songTitle}</dd>
              </div>
              <div className="review-row">
                <dt>Genre</dt>
                <dd>
                  {form.primaryGenre}
                  {form.secondaryGenre ? ` / ${form.secondaryGenre}` : ""}
                </dd>
              </div>
              <div className="review-row">
                <dt>Email</dt>
                <dd>{form.email}</dd>
              </div>
              <div className="review-row">
                <dt>Description</dt>
                <dd>{form.description}</dd>
              </div>
              {form.platforms.length > 0 ? (
                <div className="review-row">
                  <dt>Platforms</dt>
                  <dd>{form.platforms.join(", ")}</dd>
                </div>
              ) : null}
              {form.releaseDate ? (
                <div className="review-row">
                  <dt>Release Date</dt>
                  <dd>{form.releaseDate}</dd>
                </div>
              ) : null}
            </dl>

            <div
              className="card"
              style={{
                marginTop: 20,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div>
                <strong>Complete Release Pack</strong>
                <p style={{ color: "var(--text-muted)", fontSize: "0.85rem" }}>
                  One-time payment, no subscription.
                </p>
              </div>
              <div className="price-amount" style={{ fontSize: "1.6rem" }}>
                ${priceDisplay}
              </div>
            </div>

            {config?.turnstileSiteKey ? (
              <div style={{ marginTop: 20 }}>
                <TurnstileWidget
                  siteKey={config.turnstileSiteKey}
                  onVerify={setTurnstileToken}
                />
              </div>
            ) : null}

            {submitError ? (
              <div className="alert alert-error" style={{ marginTop: 20 }}>
                {submitError}
              </div>
            ) : null}

            <div className="status-actions" style={{ marginTop: 28 }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setStep("form")}
                disabled={submitting}
              >
                Edit Details
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleGenerate}
                disabled={
                  submitting || (Boolean(config?.turnstileSiteKey) && !turnstileToken)
                }
              >
                {submitting ? "Redirecting to checkout…" : "Generate My Campaign"}
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
